// Single source of truth for operator documentation — shared by OperatorGuideModal (the full,
// manually-opened reference) and the ":" trigger's autocomplete (equationCompletions.ts's
// makeOperatorSource, which shows each entry's `syntax` inline as the dropdown row's detail
// line) so a user typing an equation sees the shape of an operator without needing to open the
// guide, and so the two can never drift out of sync with each other.
export interface IOperatorGuideEntry {
  name: string;
  syntax: string;
  description: string;
  example: string | string[];
  notes?: string[];
}

export const RESPONSE_OPERATORS: IOperatorGuideEntry[] = [
  {
    name: "AND",
    syntax: "AND(response_variable_1, response_variable_2, ...)",
    description:
      "Passes only when every response you list has been answered. Insert response variables with the # shortcut.",
    example: "AND(Minor_waivfail_Granted, Minor_waivfail_Age_18_or_older)",
    notes: [
      "You can nest another operator's result inside AND, e.g. AND(OR(a, b), c).",
    ],
  },
  {
    name: "OR",
    syntax: "OR(response_variable_1, response_variable_2, ...)",
    description:
      "Passes when at least one of the listed responses has been answered.",
    example: "OR(Minor_waivfail_Granted, Minor_waivfail_Denied)",
    notes: ["Nesting works the same way as it does in AND."],
  },
  {
    name: "EQUAL",
    syntax: 'EQUAL(response_variable, "value")',
    description:
      "Passes when the answer to a response matches the value you give it. Not case-sensitive — \"Judicial\" and \"judicial\" are treated the same.",
    example: 'EQUAL(Minor_waivfail_Type, "Judicial")',
  },
  {
    name: "NOTEQUAL",
    syntax: 'NOTEQUAL(response_variable, "value")',
    description:
      "Passes when the answer to a response does NOT match the value you give it.",
    example: 'NOTEQUAL(Minor_waivfail_Type, "Judicial")',
  },
  {
    name: "COMPARE",
    syntax: 'COMPARE(response_variable, operator, "value"[, type])',
    description:
      "Compares a response's answer against a value using =, !=, <, >, >=, or <=. Use this instead of EQUAL/NOTEQUAL whenever you need a greater-than or less-than check.",
    example: [
      'COMPARE(Minor_waivfail_Age, >=, "18")',
      'COMPARE(Event_date, >=, "08/21/2026", DATE)',
    ],
    notes: [
      "By default the comparison is numeric, so value should be a plain number — non-numeric answers simply won't match.",
      'Add DATE as a fourth parameter to compare dates instead, e.g. COMPARE(Event_date, >=, "08/21/2026", DATE). Saved answers are read as YYYY-MM-DD, then MM/DD/YYYY, then a full written-out date, in that order — an answer in none of these forms won\'t match.',
    ],
  },
];

export const QUESTION_OPERATORS: IOperatorGuideEntry[] = [
  {
    name: "QAND",
    syntax: "QAND(question_variable)",
    description:
      "Passes only if every option under a question has been answered — a shortcut for writing AND across a whole question at once. Insert the question variable with the @ shortcut.",
    example: "QAND(Minor_waivfail)",
  },
  {
    name: "QOR",
    syntax: "QOR(question_variable)",
    description:
      "Passes if at least one option under a question has been answered — a shortcut for writing OR across a whole question at once.",
    example: "QOR(Minor_waivfail)",
  },
  {
    name: "QHASANSWER",
    syntax: "QHASANSWER(question_variable)",
    description:
      "Passes if the question has been answered at all, no matter which option was chosen.",
    example: "QHASANSWER(Minor_waivfail)",
  },
  {
    name: "QHASNOTANSWER",
    syntax: "QHASNOTANSWER(question_variable)",
    description: "Passes if the question was left unanswered.",
    example: "QHASNOTANSWER(Minor_waivfail)",
  },
];

export const OTHER_OPERATORS: IOperatorGuideEntry[] = [
  {
    name: "IMPORT",
    syntax: "IMPORT(rank_name)",
    description:
      "Reuses another rank's already-computed pass/fail result as if it were a variable. Useful for building tiers, where a higher rank must meet everything a lower rank requires, plus more. Insert the rank name with the $ shortcut.",
    example: "AND(IMPORT(Gold), QOR(Additional_criteria))",
    notes: [
      "A rank can only import an equal-or-lower rank — importing a higher rank isn't allowed.",
      "The rank being imported needs its own valid, saved equation for this to work correctly.",
    ],
  },
];

export const ALL_OPERATOR_GUIDE_ENTRIES: IOperatorGuideEntry[] = [
  ...RESPONSE_OPERATORS,
  ...QUESTION_OPERATORS,
  ...OTHER_OPERATORS,
];
